import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

const BRAND_ORANGE = '#f05a28';
const BRAND_ORANGE_DARK = '#d84f22';
const BRAND_TEXT = '#242424';

const AddCustomerAccess = ({ open, onClose, onSuccess }) => {
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Project picklist state
  const currentYear = new Date().getFullYear();
  const [picklist, setPicklist] = useState([]);
  const [loadingPicklist, setLoadingPicklist] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [picklistStart, setPicklistStart] = useState(`${currentYear}-01-01`);
  const [picklistEnd, setPicklistEnd] = useState(`${currentYear}-12-31`);

  const inputSx = {
    '& label.Mui-focused': { color: BRAND_ORANGE },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: '#d9d9d9' },
      '&:hover fieldset': { borderColor: BRAND_ORANGE },
      '&.Mui-focused fieldset': { borderColor: BRAND_ORANGE_DARK },
    },
  };

  useEffect(() => {
    const functions = getFunctions();
    if (process.env.NODE_ENV === 'development') {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchContacts();
      setSelectedContactId('');
      setEmail('');
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setSelectedProjectIds([]);
      setProjectSearch('');
      setPicklistStart(`${currentYear}-01-01`);
      setPicklistEnd(`${currentYear}-12-31`);
      setError(null);
      setSuccess(false);
      fetchPicklist(`${currentYear}-01-01`, `${currentYear}-12-31`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchContacts = async () => {
    setLoadingContacts(true);
    setError(null);
    try {
      const functions = getFunctions();
      const fetchContactsFunction = httpsCallable(functions, 'fetchContacts');
      const response = await fetchContactsFunction();
      if (response.data.success) {
        setContacts(response.data.data || []);
      } else {
        setError('Failed to fetch contacts');
      }
    } catch (err) {
      console.error('[AddCustomerAccess] Error fetching contacts:', err);
      setError(`Failed to fetch contacts: ${err.message}`);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchPicklist = async (start, end) => {
    const s = start || picklistStart;
    const e = end || picklistEnd;
    setLoadingPicklist(true);
    try {
      const functions = getFunctions();
      const listFn = httpsCallable(functions, 'listRentmanProjectsForPicklist');
      const response = await listFn({
        startDate: dayjs(s).startOf('day').toISOString(),
        endDate: dayjs(e).endOf('day').toISOString(),
      });
      if (response.data.success) {
        setPicklist(
          (response.data.data || [])
            .slice()
            .sort((a, b) => (a.displayname || '').localeCompare(b.displayname || '')),
        );
      }
    } catch (err) {
      console.error('[AddCustomerAccess] Error fetching project picklist:', err);
    } finally {
      setLoadingPicklist(false);
    }
  };

  const filteredPicklist = useMemo(() => {
    if (!projectSearch) return picklist;
    const q = projectSearch.toLowerCase();
    return picklist.filter((p) => (p.displayname || '').toLowerCase().includes(q));
  }, [picklist, projectSearch]);

  const toggleProject = (id) => {
    setSelectedProjectIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!selectedContactId) {
      setError('Please select a contact');
      return;
    }
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!selectedProjectIds.length) {
      setError('Please select at least one project');
      return;
    }

    setLoading(true);
    try {
      const functions = getFunctions();
      const createCustomerUser = httpsCallable(functions, 'createCustomerUser');
      const response = await createCustomerUser({
        email,
        password,
        rentmanContactId: parseInt(selectedContactId),
        username: username || null,
        contactName: selectedContact?.displayname || null,
        allowedProjectIds: selectedProjectIds,
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to create customer account');
      }
    } catch (err) {
      console.error('[AddCustomerAccess] Error creating customer:', err);
      setError(err.message || 'Failed to create customer account');
    } finally {
      setLoading(false);
    }
  };

  const selectedContact = contacts.find((c) => c.id.toString() === selectedContactId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ color: BRAND_ORANGE, fontWeight: 700 }}>
          Add Customer Access
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success">
                Customer account created successfully!
              </Alert>
            )}

            <FormControl fullWidth required disabled={loadingContacts || loading} sx={inputSx}>
              <InputLabel>Rentman Contact</InputLabel>
              <Select
                value={selectedContactId}
                onChange={(e) => setSelectedContactId(e.target.value)}
                label="Rentman Contact"
              >
                {loadingContacts ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading contacts...
                  </MenuItem>
                ) : contacts.length === 0 ? (
                  <MenuItem disabled>No contacts found</MenuItem>
                ) : (
                  contacts.map((contact) => (
                    <MenuItem key={contact.id} value={contact.id.toString()}>
                      {contact.displayname}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              disabled={loading}
              helperText="Email address for customer login (required)"
              sx={inputSx}
            />

            <TextField
              label="Username (Optional)"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              disabled={loading}
              helperText="Optional username for easier login (can use email or username to sign in)"
              sx={inputSx}
            />

            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              disabled={loading}
              helperText="Minimum 6 characters"
              sx={inputSx}
            />

            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
              disabled={loading}
              sx={inputSx}
            />

            {selectedContact && (
              <Box sx={{ p: 2, backgroundColor: 'rgba(240, 90, 40, 0.08)', borderRadius: 1 }}>
                <Typography variant="body2" color={BRAND_TEXT}>
                  Selected: <strong>{selectedContact.displayname}</strong>
                </Typography>
                <Typography variant="caption" color={BRAND_TEXT}>
                  Contact ID: {selectedContact.id}
                </Typography>
              </Box>
            )}

            {/* Project assignment */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Assign Projects ({selectedProjectIds.length} selected)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={picklistStart}
                  onChange={(e) => setPicklistStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ ...inputSx, flex: 1 }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={picklistEnd}
                  onChange={(e) => setPicklistEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ ...inputSx, flex: 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => fetchPicklist()}
                  disabled={loadingPicklist}
                  sx={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE, whiteSpace: 'nowrap', minWidth: 'auto' }}
                >
                  Load
                </Button>
              </Box>
              <TextField
                label="Search projects"
                size="small"
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
                fullWidth
                disabled={loadingPicklist}
                sx={{ ...inputSx, mb: 1 }}
              />
              <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                {loadingPicklist ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
                    <CircularProgress size={18} sx={{ color: BRAND_ORANGE }} />
                    <Typography variant="body2">Loading projects...</Typography>
                  </Box>
                ) : filteredPicklist.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No projects found
                  </Typography>
                ) : (
                  filteredPicklist.map((p) => (
                    <FormControlLabel
                      key={p.id}
                      sx={{ display: 'flex', m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedProjectIds.includes(p.id)}
                          onChange={() => toggleProject(p.id)}
                        />
                      }
                      label={<Typography variant="body2">{p.displayname}</Typography>}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || loadingContacts || !selectedContactId || !selectedProjectIds.length}
            sx={{
              backgroundColor: BRAND_ORANGE,
              '&:hover': { backgroundColor: BRAND_ORANGE_DARK },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              'Create Account'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddCustomerAccess;

